import { Form } from 'antd'
import { useRecoilState } from 'recoil'
import message from '@/libs/message'

import { useEffect, useMemo, useState } from 'react'
import createClaimApprovalLevelModalAtom from '@/recoil/createClaimApprovalLevelModal'
import { useCreateBackofficeApproverCondition } from '@/services/healthClaim/useCreateBackofficeApproverCondition'
import { useUpdateBackofficeApproverCondition } from '@/services/healthClaim/useUpdateBackofficeApproverCondition'

export default function CreateApproveLevelClaimModalHook() {
  const [createClaimApprovalLevelModal, setCreateClaimApprovalLevelModal] = useRecoilState(
    createClaimApprovalLevelModalAtom,
  )

  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true)

  const {
    mutateAsync: createBackofficeApproverConditionMutate,
    isPending: isPendingApproverCondition,
  } = useCreateBackofficeApproverCondition()

  const {
    mutateAsync: updateBackofficeApproverConditionMutate,
    isPending: isPendingUpdateApproverCondition,
  } = useUpdateBackofficeApproverCondition()

  const [form] = Form.useForm()

  type OnSubmitProps = {
    approvalLevelName: string
    amountMin: number
    amountMax: number
  }

  const onFinish = async (values: OnSubmitProps) => {
    const payload: OnSubmitProps = {
      approvalLevelName: values.approvalLevelName,
      amountMin: values.amountMin,
      amountMax: values.amountMax,
    }

    // check validate min max
    if (payload.amountMin >= payload.amountMax) {
      form.setFields([
        {
          name: 'amountMin',
          errors: ['จำนวนเงินขั้นต่ำ ต้องน้อยกว่าจำนวนเงินสูงสุด'],
        },
      ])

      return form.setFields([
        {
          name: 'amountMax',
          errors: ['จำนวนเงินขั้นต่ำ ต้องน้อยกว่าจำนวนเงินสูงสุด'],
        },
      ])
    }

    try {
      const record = createClaimApprovalLevelModal?.record
      if (record && record.id) {
        const updatePayload = { id: record.id, ...payload }

        await updateBackofficeApproverConditionMutate(updatePayload)
      } else {
        await createBackofficeApproverConditionMutate(payload)
      }

      handleCloseModal()

      if (createClaimApprovalLevelModal?.onSuccess) createClaimApprovalLevelModal.onSuccess()
      return message.success('บันทึกสำเร็จ')
    } catch (error: any) {
      // Try to extract server message(s) robustly
      const rawServerData =
        error?.response?.data?.data?.message || error?.response?.data?.message || error?.message

      const MAPPING_MESSAGE: Record<string, string> = {
        'approvalLevelName duplicated': 'ชื่อระดับการอนุมัติซ้ำ',
        'amountMin duplicated': 'จำนวนเงินขั้นต่ำซ้ำ',
        'amountMax duplicated': 'จำนวนเงินสูงสุดซ้ำ',
      }

      // Normalize to array of strings
      const serverMessages: string[] = Array.isArray(rawServerData)
        ? rawServerData.map((s) => String(s))
        : rawServerData
        ? [String(rawServerData)]
        : []

      if (serverMessages.length > 0) {
        const fieldsToSet: { name: string; errors: string[] }[] = []

        for (const msg of serverMessages) {
          const mapped = MAPPING_MESSAGE[msg]
          if (!mapped) continue

          if (msg === 'approvalLevelName duplicated') {
            fieldsToSet.push({ name: 'approvalLevelName', errors: [mapped] })
          } else if (msg === 'amountMin duplicated') {
            fieldsToSet.push({ name: 'amountMin', errors: [mapped] })
          } else if (msg === 'amountMax duplicated') {
            fieldsToSet.push({ name: 'amountMax', errors: [mapped] })
          }
        }

        if (fieldsToSet.length > 0) {
          form.setFields(fieldsToSet)
          return
        }
      }

      // Default error message
      return message.error({ content: `เกิดข้อผิดพลาด`, duration: 10 })
    }
  }

  const handleCloseModal = () => {
    setIsSubmitDisabled(true)
    form.setFieldsValue({
      approvalLevelName: undefined,
      amountMin: undefined,
      amountMax: undefined,
    })
    setCreateClaimApprovalLevelModal({
      open: false,
    })
  }

  const onCancelModal = () => {
    handleCloseModal()
  }

  const onHandleSubmit = async () => {
    try {
      const values = await form.validateFields()
      await onFinish(values) // reuse handler ที่ทำ API/close modal
    } catch {
      // validation failed -> keep modal open
      handleCloseModal()
    }
  }

  // Reset form fields when modal is closed
  useMemo(() => {
    if (!createClaimApprovalLevelModal.open) {
      form.resetFields()
    }
  }, [createClaimApprovalLevelModal.open, form])

  useEffect(() => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0)
    const values = form.getFieldsValue(['approvalLevelName', 'amountMin', 'amountMax'])
    const allFilled = ['approvalLevelName', 'amountMin', 'amountMax'].every((k) => {
      const v = (values as any)[k]
      return v !== undefined && v !== null && v !== ''
    })
    setIsSubmitDisabled(!allFilled || hasErrors)
  }, [form])

  useEffect(() => {
    const record = createClaimApprovalLevelModal.record
    if (record) {
      // map possible fields from record to form fields
      const approvalLevelName =
        (record as any).approvalLevelName ?? (record as any).name ?? undefined

      //REMARK: prefer explicit amountMin/amountMax if provided, otherwise try to parse amountDisplay like "0 - 100,000 บาท"
      let amountMin = (record as any).amountMin ?? undefined
      let amountMax = (record as any).amountMax ?? undefined

      if ((amountMin === undefined || amountMax === undefined) && (record as any).amountDisplay) {
        const display = String((record as any).amountDisplay)
        const matches = display.match(/([0-9,]+)\s*-\s*([0-9,]+)/)
        if (matches) {
          amountMin = Number(matches[1].replace(/,/g, ''))
          amountMax = Number(matches[2].replace(/,/g, ''))
        }
      }

      form.setFieldsValue({
        approvalLevelName: approvalLevelName || undefined,
        amountMin: amountMin ?? undefined,
        amountMax: amountMax ?? undefined,
      })
    } else {
      form.resetFields()
    }
  }, [createClaimApprovalLevelModal.record, form])

  const isPendingApprover = isPendingUpdateApproverCondition || isPendingApproverCondition

  return {
    createClaimApprovalLevelModal,
    onCancelModal,
    form,
    onFinish,
    onHandleSubmit,
    isPendingApprover,
    isSubmitDisabled,
    setIsSubmitDisabled,
  }
}
